import React, { useEffect, useRef, ReactNode } from 'react';
// @ts-ignore
import 'bootstrap/js/dist/dropdown';
import Collapse from 'bootstrap/js/dist/collapse';
import cn from 'classnames';
import type { NavItem, INavProps } from 'tbk-components/src/components/Nav';
import { useRandomID } from '../../utils';

/**
 * Nav
 * @requiresHooks
 */
const Nav: React.FC<INavProps> = ({
	navItems,
	children,
	className,
	classNames = [],
	justifyContent = 'start',
}) => {
	// On Heemans, when trying to render the Footer in the SSR server, I'm seeing an error because it's
	// decoding an array prop as an object { "0": "first", "1": "second" } as opposed to [ 'first', 'second' ].
	// This might be pointing to the same thing that is causing the odd characters issue on Legate.  This is a note
	// to fix the problem ( so _navItems.map is a function always ) but also to remind myself of the thread to pull on.
	// @ts-ignore
	const _navItems = navItems ? (Array.isArray(navItems) ? navItems : Object.values(navItems)) as NavItem[] : null;
	const accordionID = useRandomID('nav');
	return (
		<ul
			className={
				className ||
				cn(
					'nav',
					`justify-content-${justifyContent}`,
					'flex-column',
					'accordion',
					...classNames,
				)
			}
			id={accordionID}
		>
			{_navItems &&
				_navItems.map((item, index) => {
					const linkArgs: any = {};
					const linkID = useRandomID('nav-link');
					const collapseID = useRandomID('collapse');
					const isDropdown =
						item.children &&
						!(
							Array.isArray(item.children) &&
							item.children.length < 1
						);

					if (item.isActive === true) {
						linkArgs['aria-current'] = 'page';
					}

					if (item.isDisabled === true) {
						linkArgs['aria-disabled'] = 'true';
						linkArgs.tabIndex = '-1';
					}

					if (isDropdown) {
						linkArgs['aria-expanded'] = 'false';
						linkArgs.role = 'button';
						linkArgs['data-bs-toggle'] = 'collapse';
						linkArgs['data-bs-target'] = `#${collapseID}`;
						linkArgs['aria-expanded'] = 'false';
						linkArgs['aria-controls'] = collapseID;
					}

					return (
						<li
							className={cn(
								'nav-item',
								isDropdown && 'dropdown',
								...(item.classNames || []),
							)}
							key={`${item.href}-${index}`}
						>
							<a
								id={linkID}
								className={cn(
									'nav-link',
									item.isActive === true && 'active',
									item.isDisabled === true && 'disabled',
									isDropdown === true &&
										'collapse-toggle',
									'collapsed',
								)}
								href={item.href}
								{...linkArgs}
							>
								{item.label}
							</a>
							{isDropdown && (
								<ul
									className={cn(
										'collapse',
									)}
									id={collapseID}
									data-bs-parent={`#${accordionID}`}
								>
									{(item.children! as NavItem[]).map(
										(item, childIndex) => (
											<li
												key={`${item.href}-${index}-${childIndex}`}
												className={cn(item.classNames || [])}
											>
												<a
													className={cn(
														item.isActive ===
																true &&
																'active',
													)}
													href={item.href}
												>
													{item.label}
												</a>
											</li>
										),
									)}
								</ul>
							)}
						</li>
					);
				})}
			{children}
		</ul>
	);
};

export default Nav;
